import { useMemo } from 'react';
import { TestSuites } from '../types/test-specification';
import { TestSpecification } from '@app.raytd.com/common/types/test-specification';
import { NO_SUBCATEGORY, normalizeSubcategoryKey } from '@/app/features/testsuites/specifications/subcategory-tier';

interface OrganizedData {
  [suite: string]: {
    [category: string]: {
      [subcategory: string]: TestSpecification[]
    }
  }
}

export function useTestSpecificationData(testSuites: TestSuites[]): OrganizedData {
  return useMemo(() => {

    if (!testSuites) {
      // is empty
      return {};
    }

    if (!Array.isArray(testSuites)) {
      console.error('Invalid testSuites input: expected array');
      return {};
    }

    const data: OrganizedData = {};

    testSuites.forEach((testSuite, suiteIndex) => {
      if (!testSuite?.specifications || !Array.isArray(testSuite.specifications)) {
        console.warn(`Invalid specifications array in suite at index ${suiteIndex}`);
        return;
      }

      // const suiteId = testSuite.title?.trim();
      const suiteId = testSuite.id?.toString();
      if (!suiteId) {
        console.warn(`Missing suite title at index ${suiteIndex}`);
        return;
      }

      testSuite.specifications.forEach((spec, specIndex) => {
        if (!spec?.category) {
          console.warn(`Invalid specification at index ${specIndex} in suite ${suiteId}`);
          return;
        }

        const category = spec.category.trim();
        // A blank sub-category collapses the tier (ADR-0009) — group it under the
        // sentinel. Dropping the spec here is what hid it from the Test Creator
        // entirely (QA 307.07).
        const subcategory = normalizeSubcategoryKey(spec.subcategory);

        data[suiteId] = data[suiteId] || {};
        data[suiteId][category] = data[suiteId][category] || {};
        data[suiteId][category][subcategory] = data[suiteId][category][subcategory] || [];

        try {
          data[suiteId][category][subcategory].push({
            ...spec,
            category,
            // Keep "absent" absent downstream rather than a blank string, so
            // display code omits the line instead of rendering an empty one.
            subcategory: subcategory === NO_SUBCATEGORY ? null : subcategory
          });
        } catch (error) {
          console.error(`Failed to add specification at index ${specIndex}:`, error);
        }
      });
    });

    return data;
  }, [testSuites]);
}
